<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  useId,
  watch,
  watchEffect,
} from 'vue'

import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import type { ColorField, SizeField } from '@render/types/styles'
import { usePaymentData, PAYMENT_DATA_PREVIEW } from '@render/composables/usePaymentData'
import { emitPayment, PAYMENT_EVENTS } from '@render/composables/usePaymentEvents'
import {
  composeShadow,
  getColorStyleData,
  getSizeStyleData,
} from '@render/composables/useStyleData'
import { isPpoPurchased, recordPurchase } from '@render/composables/usePurchasedPpos'
import { setDeviceTimestampOnce } from '@render/lib/deviceTimestamps'
import {
  CHECKOUT_LOAD_ERROR_FALLBACK,
  CHECKOUT_LOADING_FALLBACK,
  useCheckoutLoadErrorMessage,
  useCheckoutLoadingMessage,
} from '@render/composables/useCheckoutMessages'
import { useVisibilityMap } from '@render/composables/useLogic'
import RenderSlot from '@render/RenderSlot.vue'
import TipTapRenderer from '@render/lib/TipTapRenderer.vue'
import type { ComponentNode, OfferGroupDataDto } from '@api/types.gen'

import { useCheckoutTexts } from '../Checkout/useCheckoutTexts'
import type { CheckoutFields } from '../Checkout/definition'
import usePrimer, { type PrimerCheckoutCompleteData } from './usePrimer'
import { usePrimerSubmitButtonLabel } from './usePrimerSubmitButtonLabel'
import { usePrimerTextOverrides } from './usePrimerTextOverrides'
import type { PrimerCheckoutFields } from './definition'

type Model = PrimerCheckoutFields & CheckoutFields

const isBrowser = typeof window !== 'undefined'

// Primer renders inside its own iframe and has no access to the host document's CSS variables.
// Resolve any var(--x) to its computed value before passing it into primerStyle.
function resolveCssVar(value: string): string {
  if (!isBrowser) return value

  const match = value.match(/^var\(--([\w-]+)\)/)
  if (!match) return value

  return (
    getComputedStyle(document.documentElement).getPropertyValue(`--${match[1]}`).trim() || value
  )
}

function color(f: ColorField | undefined, fallback = 'transparent') {
  return getColorStyleData(f) || fallback
}
function size(f: SizeField | undefined, fallback = '0px') {
  return getSizeStyleData(f) || fallback
}
function primerColor(f: ColorField | undefined, fallback = 'transparent') {
  return resolveCssVar(color(f, fallback))
}
function primerSize(f: SizeField | undefined, fallback = '0px') {
  return resolveCssVar(size(f, fallback))
}

const fields = defineModel<Model>('fields', {
  default: () => ({}),
})
const props = defineProps<{
  slotChildren?: Record<string, ComponentNode[]>
  editable?: boolean
}>()
const pipe = usePipe()
const isEditorMode = isBrowser && window.document.getElementsByClassName('mode-editor').length > 0

// Editor-only visual mock — lazily imported so its chunk is never fetched in a
// built pipe (which is never in EDITOR mode). Gated by a single `v-if="isEditor"`
// in the template below.
const PrimerCheckoutMock = defineAsyncComponent(() => import('./PrimerCheckoutMock.vue'))

const { buildRealPaymentData } = usePaymentData(pipe, isBrowser)

// --- Exit-intent suppression during a wallet payment ---
// On mobile the only exit-intent signal is `visibilitychange → hidden`, and a
// Google Pay sheet / PayPal popup hides the tab the same way a real exit does -
// so without help, EI shows the discount popup mid-payment (worst case: a
// second charge at the exit price). While a payment is underway we tell the
// detector ([...slug].vue) to stand down.
//
// The leading edge is Primer's googlePay.onClick / paypal.onClick (wired in
// usePrimer), which fire before the sheet/popup opens and reach us even for the
// PayPal button inside its cross-origin iframe. PayPal's signal crosses that
// iframe via async postMessage, so it can land a few ms after the tab hides;
// the detector absorbs that gap with a short settle delay (see useExitIntent).
const suppressionToken = `primer-ei:${useId()}`

// Three states, because onVisible has to tell two kinds of "busy" apart:
//   'interacting' - wallet sheet/popup open, no charge yet. If the tab comes
//                   back here the user bailed, so release immediately.
//   'charging'    - Primer is creating the payment. Only a terminal callback
//                   releases; a tab-return must NOT (and we must not reinit the
//                   checkout here - that double-charges).
let phase: 'idle' | 'interacting' | 'charging' = 'idle'
let eiSuppressionMaxHoldTimer: ReturnType<typeof setTimeout> | null = null

// Backstop. Closing a Google Pay popup on desktop fires no Primer callback and
// (no tab was hidden) no visibility change, so neither natural release arrives;
// without this, suppression would stick for the rest of the visit. Normally
// cancelled by a terminal callback long before it fires.
const EI_SUPPRESSION_MAX_HOLD_MS = 120_000

function suppressExitIntent() {
  if (!isBrowser || isEditorMode) return

  pipe.suppressExitIntent(suppressionToken)

  // Clear before re-arming to not stack
  if (eiSuppressionMaxHoldTimer) clearTimeout(eiSuppressionMaxHoldTimer)

  eiSuppressionMaxHoldTimer = setTimeout(() => {
    eiSuppressionMaxHoldTimer = null
    releaseExitIntent()
  }, EI_SUPPRESSION_MAX_HOLD_MS)
}

function releaseExitIntent() {
  // Clear the pending one
  if (eiSuppressionMaxHoldTimer) {
    clearTimeout(eiSuppressionMaxHoldTimer)
    eiSuppressionMaxHoldTimer = null
  }

  phase = 'idle'
  pipe.releaseExitIntent(suppressionToken)
}

// Fires from googlePay.onClick / paypal.onClick when the user taps the wallet
// button, before the sheet/popup opens.
function onWalletPaymentStart() {
  phase = 'interacting'
  suppressExitIntent()
}

// Tab returned while a wallet sheet was open but before any charge started =>
// the user dismissed it, so recover EI now instead of waiting on the backstop.
// While 'charging' the terminal callbacks own the release, so we hold.
function onVisible() {
  if (document.visibilityState !== 'visible' || phase !== 'interacting') return
  releaseExitIntent()
}

function getPriceProductObjectId(): string | undefined {
  const isExitIntent = pipe.getData('var.global.hasExitIntentBeenAccepted')

  if (isExitIntent && fields.value.pipelineOfferType === 'BASIC') {
    return pipe.getData('var.global.selectedOfferGroup.exit.ppoId') as string | undefined
  }

  return pipe.getData('var.global.selectedOfferGroup.regular.ppoId') as string | undefined
}

// Thunk so paymentData is read AFTER pipe persisted-data load has flushed
// (see usePrimer.ts comments + usePipe.ts loadPersistedData). Captured-at-
// setup the payload would always read undefined for `var.global.*` fields
// on hard reload / after dev-tools state reset.
const paymentData = () =>
  isEditorMode
    ? PAYMENT_DATA_PREVIEW
    : buildRealPaymentData({
        pipelineOfferType: fields.value.pipelineOfferType,
        priceProductObjectId: getPriceProductObjectId(),
        restrictSaveCard: true,
        provider: 'primer',
      })

// Need styles to retrieve and then apply styles, cause iframe doesn't got access to our css
const primerStyle = computed(() => ({
  input: {
    base: {
      fontFamily: 'Avenir, Arial, sans-serif',
      color: primerColor(fields.value.textColor, 'var(--color-text-primary)'),
      fontSize: primerSize(fields.value.textSize, 'var(--font-size-lg) !important'),
      placeholder: {
        color: primerColor(
          fields.value.textColorPlaceholder,
          'var(--color-input-text-placeholder)',
        ),
      },
      focus: {
        borderColor: primerColor(fields.value.borderColorActive, 'var(--color-border-inverse)'),
        background: primerColor(fields.value.bgColorActive, 'var(--color-input-background-active)'),
      },
      error: {
        borderColor: primerColor(fields.value.borderColorError, 'var(--color-input-border-error)'),
        background: primerColor(fields.value.bgColorError, 'var(--color-input-background-error)'),
      },
    },
  },
}))
// Translated label for Primer's built-in submit button (Primer localizes it
// from its own SDK locale only; usePrimer overrides the text node with this).
// The author's per-component "Submit Button Label" field wins over the global
// `primerSubmitButton` translation; when neither is set this is `undefined`
// and Primer keeps its own localized text.
const submitButtonLabel = usePrimerSubmitButtonLabel(() => fields.value.submitButtonLabel?.key)
const texts = useCheckoutTexts(() => fields.value)
usePrimerTextOverrides('checkout-container', texts, isBrowser && !isEditorMode)

const { isLoading, isError, reinitCheckout, clearPrimerSession } = usePrimer({
  paymentData,
  styleOverrides: primerStyle,
  submitButtonLabel: () => submitButtonLabel.value,
  cardPlaceholders: () => ({
    cardNumber: texts.value.cardNumberPlaceholder,
    expiryDate: texts.value.expiryDatePlaceholder,
    cvv: texts.value.cvvPlaceholder,
    cardholderName: texts.value.cardholderNamePlaceholder,
  }),
  checkoutLoadTimeoutSeconds: () => {
    const v = fields.value.checkoutLoadTimeoutSeconds
    return v !== undefined && v > 0 ? v : undefined
  },
  shouldSkip: () => {
    const ppoId = getPriceProductObjectId()
    const skip = isPpoPurchased(pipe, ppoId)
    if (skip) {
    }
    return skip
  },
  // Veto for the composable's 'online' self-heal reinit: never while a wallet
  // sheet is open or a charge is running (a reinit would clearContainer() the
  // live form mid-payment — same hazard the exit-intent watcher guards), and
  // never in the editor preview (its payload can't pass prereqs; connectivity
  // flaps on the author's machine shouldn't poke payments-api).
  canReinit: () => !isEditorMode && phase === 'idle',
  callbackFns: {
    onWalletPaymentStart,
    onPaymentMethodAction,
    onPaymentCreationStart,
    onBeforePaymentCreate,
    onCheckoutComplete,
    onCheckoutFail,
    onUserCancellation,
    onCheckoutRendered,
    onPrimerCheckoutRequested,
    onPrimerCheckoutLoaded,
    onPrimerCheckoutOnBeforePaymentCreated,
    onPrimerCheckoutLoadedConfirmation,
    onCheckoutAbandoned,
  },
  isBrowser,
})

// --- Custom load-error content (the "error" slot) ---
// Authors can drop any components into the "On Checkout Load Error" slot;
// when the checkout fails to load, that content replaces the default message.
// With an empty slot the default message shows: the seeded
// `global.checkoutLoadError` translation, or CHECKOUT_LOAD_ERROR_FALLBACK
// when the key is absent/empty (pipe built before the key existed, or the
// language was never localized).
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

// Loading state — same slot/translation/fallback contract as the error state
// below. Authored slot content replaces the default "Loading checkout..."
// message while the checkout loads; an empty (or fully rule-hidden) slot falls
// back to the seeded `global.checkoutLoading` translation, then
// CHECKOUT_LOADING_FALLBACK. Slot is always visible in the editor for authoring.
const hasLoadingSlotContent = computed(() => (props.slotChildren?.loading?.length ?? 0) > 0)
const loadingSlotVisibility = useVisibilityMap(
  computed(() => ({ children: props.slotChildren?.loading ?? [] })),
  pipe.getData,
)
const hasVisibleLoadingSlotContent = computed(() =>
  (props.slotChildren?.loading ?? []).some((c) => loadingSlotVisibility.value[c.nodeId]),
)
const showLoadingSlot = computed(
  () => isEditor.value || (isLoading.value && hasLoadingSlotContent.value),
)
const showDefaultLoading = computed(
  () => !isEditor.value && isLoading.value && !hasVisibleLoadingSlotContent.value,
)
const loadingMessageDoc = useCheckoutLoadingMessage()

const hasErrorSlotContent = computed(() => (props.slotChildren?.error?.length ?? 0) > 0)
// Rule-hidden slot children render nothing, so "slot has content" must use
// the same visibility evaluation the renderer applies — otherwise a slot
// whose every child is hidden by a visibility rule would suppress the
// default message and leave a load error with no feedback at all.
const errorSlotVisibility = useVisibilityMap(
  computed(() => ({ children: props.slotChildren?.error ?? [] })),
  pipe.getData,
)
const hasVisibleErrorSlotContent = computed(() =>
  (props.slotChildren?.error ?? []).some((c) => errorSlotVisibility.value[c.nodeId]),
)
// Slot content mounts only on a real load error (never mid-reinit while the
// spinner shows) — except in the editor, where it stays visible so it can be
// authored (mirrors NutritionistChat's `complete` slot).
const showErrorSlot = computed(
  () => isEditor.value || (!isLoading.value && isError.value && hasErrorSlotContent.value),
)
// !isEditor: the editor preview's checkout always errors (its payload can't
// pass prereqs), which would stack the default message on top of the slot's
// authoring placeholder — a misleading signal, since at runtime the two are
// mutually exclusive.
const showDefaultError = computed(
  () => !isEditor.value && !isLoading.value && isError.value && !hasVisibleErrorSlotContent.value,
)
const errorMessageDoc = useCheckoutLoadErrorMessage()

let reinitTimer: ReturnType<typeof setTimeout>

const stopWatch = watch(
  () => JSON.stringify(primerStyle.value),
  () => {
    clearTimeout(reinitTimer)
    reinitTimer = setTimeout(() => reinitCheckout(), 1000)
  },
)

// When exit intent is accepted on the same page as the checkout (BASIC type),
// the PPO switches from regular → exit pricing. Clear the cached session so
// usePrimer fetches a fresh token bound to the new PPO, then reinit.
//
// Guard: skip if the checkout is still in its initial loading phase. On page
// refresh, a previously-persisted `hasExitIntentBeenAccepted=true` value loads
// from localStorage concurrently with the initial session fetch, which would
// trigger a duplicate payments-api call and render two checkouts. The initial
// fetch already reads the correct (exit) PPO via `getPriceProductObjectId()`, so
// no reinit is needed — only react when the user accepts exit intent live.
const stopExitIntentWatch = watch(
  () => pipe.getData('var.global.hasExitIntentBeenAccepted'),
  (accepted) => {
    if (!accepted || isEditorMode || fields.value.pipelineOfferType !== 'BASIC') return
    if (isLoading.value) return

    // Don't reinit the checkout while a payment is in flight - any non-idle
    // phase. 'charging' means a charge is being created (repricing would
    // double-charge at the exit price); 'interacting' means the wallet popup is
    // still open (a reinit would rip the checkout out from under the user).
    // Only reprice when idle. Belt-and-braces anyway: EI is suppressed in both
    // states, so the popup can't show and `accepted` shouldn't flip here at all.
    if (phase !== 'idle') return

    clearPrimerSession()
    reinitCheckout()
  },
)

// User has reached a checkout component → VISIT_CHECKOUT (legacy parity:
// /workspace/pipelines/lib/common/js/components/commonBasicPlanCheckout.js#L145).
// Pipeorders integration translates this event into a PUBLIC transitionOrder()
// POST. Self-loops (re-mounts on the same checkout page) are accepted by the
// transition matrix.
//
// `await pipe.whenReady()` — without it, on hard reload pipeorders would see
// `var.global.email` as undefined and silently drop the VISIT_CHECKOUT
// transition (the localStorage→variables merge runs inside `setTimeout(0)`
// for hydration safety, after onMounted has already fired).
onMounted(async () => {
  if (isBrowser && !isEditorMode) document.addEventListener('visibilitychange', onVisible)

  await pipe.whenReady()
  emitPayment({ pipe, event: PAYMENT_EVENTS.checkoutVisited })
})

// Once the primerStyles is updated reinit the checkout for changes to take place, otherwise need cmd + R, meaning, refresh
onUnmounted(() => {
  stopWatch()
  stopExitIntentWatch()
  clearTimeout(reinitTimer)

  if (isBrowser) document.removeEventListener('visibilitychange', onVisible)

  // Release EI on unmount so it can't get stuck suppressed.
  releaseExitIntent()
})

const css = computed(() => ({
  // Checkout
  textSize: size(fields.value.textSize, 'var(--font-size-lg)'),
  textColor: color(fields.value.textColor, 'var(--color-text-primary)'),
  textColorPlaceholder: color(
    fields.value.textColorPlaceholder,
    'var(--color-input-text-placeholder)',
  ),
  bgColorActive: color(fields.value.bgColorActive, 'var(--color-input-background-active)'),
  bgColorError: color(fields.value.bgColorError, 'var(--color-input-background-error)'),
  borderColorActive: color(fields.value.borderColorActive, 'var(--color-border-inverse)'),
  borderColorError: color(fields.value.borderColorError, 'var(--color-input-border-error)'),
  bgColor: color(fields.value.bgColor, 'var(--color-input-background-default)'),
  bgColorHover: color(fields.value.bgColorHover, 'var(--color-input-background-hover)'),
  errorMessageColor: color(fields.value.errorMessageColor, 'var(--color-feedback-error-text)'),
  labelFontSize: size(fields.value.labelSize, 'var(--font-size-xs)'),
  labelColor: color(fields.value.labelColor, 'var(--color-text-primary)'),
  borderRadius: size(fields.value.borderRadius, '4px'),
  borderWidth: size(fields.value.borderWidth, '1px'),
  borderColor: color(fields.value.borderColor, 'var(--color-border-inverse)'),
  paddingY: size(fields.value.paddingY, '0px'),
  paddingX: size(fields.value.paddingX, '0px'),

  // External buttons
  marginTopExternalPaymentButtons: size(fields.value.marginTopExternalPaymentButtons, '0px'),
  marginBottomExternalPaymentButtons: size(fields.value.marginBottomExternalPaymentButtons, '32px'),
  gapBetweenExternalPaymentButtons: size(fields.value.gapBetweenExternalPaymentButtons, '16px'),

  // Submit button
  bgColorSubmitButton: color(
    fields.value.bgColorSubmitButton,
    'var(--color-action-primary-background)',
  ),
  bgColorHoverSubmitButton: color(
    fields.value.bgColorHoverSubmitButton,
    'var(--color-action-primary-background-hover)',
  ),
  bgColorPressedSubmitButton: color(
    fields.value.bgColorPressedSubmitButton,
    'var(--color-action-primary-background-pressed)',
  ),
  bgColorDisabledSubmitButton: color(
    fields.value.bgColorDisabledSubmitButton,
    'var(--color-action-primary-background-disabled)',
  ),
  textSizeSubmitButton: size(fields.value.textSizeSubmitButton, 'var(--font-size-lg)'),
  textAlignSubmitButton: fields.value.textAlignSubmitButton || 'center',
  textColorSubmitButton: color(
    fields.value.textColorSubmitButton,
    'var(--color-action-primary-text)',
  ),
  textColorHoverSubmitButton: getColorStyleData(fields.value.textColorHoverSubmitButton),
  textColorPressedSubmitButton: getColorStyleData(fields.value.textColorPressedSubmitButton),
  textColorDisabledSubmitButton: color(
    fields.value.textColorDisabledSubmitButton,
    'var(--color-action-primary-text-disabled)',
  ),
  borderRadiusSubmitButton: size(fields.value.borderRadiusSubmitButton, 'var(--radius-xs)'),
  borderWidthSubmitButton: size(fields.value.borderWidthSubmitButton, '1px'),
  borderColorSubmitButton: color(
    fields.value.borderColorSubmitButton,
    'var(--color-action-primary-border)',
  ),
  borderColorHoverSubmitButton: color(
    fields.value.borderColorHoverSubmitButton,
    'var(--color-action-primary-border)',
  ),
  borderColorPressedSubmitButton: color(
    fields.value.borderColorPressedSubmitButton,
    'var(--color-action-primary-pressed)',
  ),
  borderColorDisabledSubmitButton: color(
    fields.value.borderColorDisabledSubmitButton,
    'var(--color-action-primary-disabled)',
  ),
  paddingYSubmitButton: size(fields.value.paddingYSubmitButton, 'var(--space-8)'),
  paddingXSubmitButton: size(fields.value.paddingXSubmitButton, 'var(--space-24)'),
  gapColumnsSubmitButton: size(fields.value.gapColumnsSubmitButton, 'var(--space-12)'),
  gapRowsSubmitButton: size(fields.value.gapRowsSubmitButton, 'var(--space-12)'),
  marginTopSubmitButton: size(fields.value.marginTopSubmitButton, 'var(--space-24)'),
  marginBottomSubmitButton: size(fields.value.marginBottomSubmitButton, 'var(--space-32)'),
  shadowIdleSubmitButton: composeShadow(
    fields.value.dropShadowSubmitButton,
    fields.value.innerShadowSubmitButton,
    'var(--shadow-cta-button-default)',
    'var(--shadow-inner-glow-cta-button-default)',
  ),
  shadowHoverSubmitButton: composeShadow(
    fields.value.hoverDropShadowSubmitButton,
    fields.value.hoverInnerShadowSubmitButton,
  ),
  shadowPressedSubmitButton: composeShadow(
    fields.value.pressedDropShadowSubmitButton,
    fields.value.pressedInnerShadowSubmitButton,
    'none',
    'none',
  ),
  minHeightSubmitButton: size(fields.value.minHeightSubmitButton, '78px'),
  maxWidthSubmitButton: size(fields.value.maxWidthSubmitButton, '480px'),
}))

const emit = defineEmits<{
  (e: 'onPaymentMethodAction'): void
  (e: 'onPaymentCreationStart'): void
  (e: 'onBeforePaymentCreate'): void
  (e: 'onCheckoutComplete'): void
  (e: 'onCheckoutFail'): void
  (e: 'onCheckoutRendered'): void
  (e: 'onPrimerCheckoutRequested'): void
  (e: 'onPrimerCheckoutLoaded'): void
  (e: 'onPrimerCheckoutOnBeforePaymentCreated'): void
  (e: 'onPrimerCheckoutLoadedConfirmation'): void
}>()

function onPaymentMethodAction() {
  emit('onPaymentMethodAction')
}

// Primer's onPaymentCreationStart fires when the user clicks Pay — legacy
// primer.js#L188 mapped this to SUBMIT_CC_INPUT.
function onPaymentCreationStart() {
  phase = 'charging'
  suppressExitIntent()
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.checkoutStarted,
    extra: { pipelineOfferType: fields.value.pipelineOfferType },
  })
  emit('onPaymentCreationStart')
}

function onBeforePaymentCreate() {
  phase = 'charging'
  suppressExitIntent()
  emit('onBeforePaymentCreate')
}

function onCheckoutComplete(data?: PrimerCheckoutCompleteData) {
  // Stash the NPS payment id on the pipe so the pipeorders integration can
  // forward it to the dev-only NPS webhook replay endpoint
  // (orders/api/src/modules/dev-tools/nps-payments-dev-mock). The flag is
  // ignored by all other consumers.
  if (data?.payment?.id) {
    pipe.setData('var.global.lastPaymentId', data.payment.id)
  }
  // Record the completed PPO + offergroup alternates so a back-navigation
  // revisit short-circuits via `shouldSkip` instead of double-charging.
  // Guard against replayed callbacks (expired token, back-navigation) firing
  // analytics a second time — recordPurchase is what sets the isPpoPurchased
  // flag, so checking it first makes both record + emit idempotent.
  const offerGroup = pipe.getData('var.global.selectedOfferGroup') as OfferGroupDataDto | null
  const ppoId = getPriceProductObjectId()
  if (!isPpoPurchased(pipe, ppoId)) {
    recordPurchase(pipe, offerGroup, ppoId, fields.value.pipelineOfferType)
    setDeviceTimestampOnce(pipe, 'paymentCompleted')
    emitPayment({
      pipe,
      event: PAYMENT_EVENTS.checkoutCompleted,
      extra: { pipelineOfferType: fields.value.pipelineOfferType },
    })
  }
  emit('onCheckoutComplete')
  // Use-once invalidation. Mirrors legacy clearPrimerSession() at
  // https://github.com/cppv3/pipelines/blob/main/lib/ckd-kg-f/js/dataStore.js#L479-L482.
  clearPrimerSession()
}

function onCheckoutFail() {
  releaseExitIntent()
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.checkoutFailed,
    extra: { reason: 'primer_checkout_failed', pipelineOfferType: fields.value.pipelineOfferType },
  })
  emit('onCheckoutFail')
  clearPrimerSession()
  reinitCheckout()
}

// Primer surfaces a wallet popup dismissal (e.g. PayPal "window closed") as a
// soft cancellation - no payment was created, so just drop the in-flight state
// and release the exit-intent block.
function onUserCancellation() {
  releaseExitIntent()
}

// Primer iframe finished rendering — legacy primer.js#L111 mapped this to
// INITIATE_CHECKOUT.
function onCheckoutRendered() {
  emitPayment({ pipe, event: PAYMENT_EVENTS.checkoutRendered })
  emit('onCheckoutRendered')
}

function onPrimerCheckoutRequested() {
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.trackingCheckoutRequested,
    customData: { eventName: 'Primer Checkout Requested' },
  })
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.checkoutRequested,
    extra: { pipelineOfferType: fields.value.pipelineOfferType, checkoutProvider: 'primer' },
  })
  emit('onPrimerCheckoutRequested')
}

function onPrimerCheckoutLoaded() {
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.trackingCheckoutLoaded,
    customData: { eventName: 'Primer Checkout Loaded' },
  })
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.checkoutLoaded,
    extra: { pipelineOfferType: fields.value.pipelineOfferType, checkoutProvider: 'primer' },
  })
  emit('onPrimerCheckoutLoaded')
}

function onPrimerCheckoutOnBeforePaymentCreated(customData: Record<string, unknown>) {
  pipe.emit(PAYMENT_EVENTS.trackingCheckoutBeforePaymentCreated, customData)
  emit('onPrimerCheckoutOnBeforePaymentCreated')
}

function onPrimerCheckoutLoadedConfirmation() {
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.trackingCheckoutLoadedConfirmation,
    customData: { eventName: 'Primer Checkout Loaded Confirmation' },
  })
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.checkoutLoadedConfirmation,
    extra: { pipelineOfferType: fields.value.pipelineOfferType, checkoutProvider: 'primer' },
  })
  emit('onPrimerCheckoutLoadedConfirmation')
}

// Fires alongside usePrimer's existing Bugsnag abandonment report — checkout
// was requested but the user left before the card form/confirmation loaded.
function onCheckoutAbandoned() {
  emitPayment({
    pipe,
    event: PAYMENT_EVENTS.checkoutAbandoned,
    extra: {
      reason: 'primer_checkout_abandoned',
      pipelineOfferType: fields.value.pipelineOfferType,
      checkoutProvider: 'primer',
    },
  })
}

watchEffect(() => {
  if (!isBrowser) return

  const id = 'primer-custom-styles'
  let el = document.getElementById(id) as HTMLStyleElement
  if (!el) {
    el = document.createElement('style')
    el.id = id
    document.head.appendChild(el)
  }

  el.textContent = `
    @supports (-moz-appearance: none) {
      #checkout-container input,
      #checkout-container textarea,
      #checkout-container select,
      #checkout-container iframe {
        touch-action: manipulation;
      }
    }

    /* Checkout styles  *\/
    .PrimerCheckout__label {
      font-size: ${css.value.labelFontSize} !important;
      color: ${css.value.labelColor} !important;
    }
    #primer-checkout-card-number,
    #primer-checkout-card-expirydate-error,
    #primer-checkout-card-cvv-error,
    #primer-checkout-cardholder-name-error {
      color: ${css.value.errorMessageColor};
    }
    #primer-checkout-card-number-field,
    #primer-checkout-card-expirydate-field,
    #primer-checkout-card-cvv-field,
    #primer-checkout-cardholder-name-field {
      padding: ${css.value.paddingY} ${css.value.paddingX};
    }
    #primer-checkout-card-number-field > div:nth-child(2),
    #primer-checkout-card-expirydate-field > div:nth-child(2),
    #primer-checkout-card-cvv-field > div:nth-child(2),
    #primer-checkout-cardholder-name-field > div:nth-child(2) {
      background-color: ${css.value.bgColor};
      color: ${css.value.textColor};
      border-color: ${css.value.borderColor};
      border-width: ${css.value.borderWidth};
      border-radius: ${css.value.borderRadius};
    }
    #primer-checkout-card-number-field > div:nth-child(2):hover,
    #primer-checkout-card-expirydate-field > div:nth-child(2):hover,
    #primer-checkout-card-cvv-field > div:nth-child(2):hover,
    #primer-checkout-cardholder-name-field > div:nth-child(2):hover {
      background-color: ${css.value.bgColorHover};
    }

    div#primer-checkout-apm-button-container {
      display: flex;
      flex-direction: column;
      margin-top: ${css.value.marginTopExternalPaymentButtons};
      margin-bottom: ${css.value.marginBottomExternalPaymentButtons};
      gap: ${css.value.gapBetweenExternalPaymentButtons};
    }

    .PrimerCheckout__apmButton:not(:first-of-type) {
      margin-top: unset !important;
    }

    /* Submit button styles */
    button#primer-checkout-submit-button {
      min-height: ${css.value.minHeightSubmitButton};
      max-width: ${css.value.maxWidthSubmitButton};
      background-color: ${css.value.bgColorSubmitButton};
      color: ${css.value.textColorSubmitButton};
      font-size: ${css.value.textSizeSubmitButton};
      text-align: ${css.value.textAlignSubmitButton};
      border-radius: ${css.value.borderRadiusSubmitButton};
      border-width: ${css.value.borderWidthSubmitButton};
      border-color: ${css.value.borderColorSubmitButton};
      padding: ${css.value.paddingYSubmitButton} ${css.value.paddingXSubmitButton};
      gap: ${css.value.gapRowsSubmitButton} ${css.value.gapColumnsSubmitButton};
      margin: 0 auto;
      margin-top: ${css.value.marginTopSubmitButton};
      margin-bottom: ${css.value.marginBottomSubmitButton};
      box-shadow: ${css.value.shadowIdleSubmitButton};
    }
    button#primer-checkout-submit-button:hover {
      background-color: ${css.value.bgColorHoverSubmitButton};
      border-color: ${css.value.borderColorHoverSubmitButton};
      ${css.value.textColorHoverSubmitButton ? `color: ${css.value.textColorHoverSubmitButton};` : ''}
      opacity: 1;
      box-shadow: ${css.value.shadowHoverSubmitButton};
    }
    button#primer-checkout-submit-button:active {
      background-color: ${css.value.bgColorPressedSubmitButton};
      border-color: ${css.value.borderColorPressedSubmitButton};
      ${css.value.textColorPressedSubmitButton ? `color: ${css.value.textColorPressedSubmitButton};` : ''}
      box-shadow: ${css.value.shadowPressedSubmitButton};
    }
    button#primer-checkout-submit-button:disabled {
      background-color: ${css.value.bgColorDisabledSubmitButton};
      color: ${css.value.textColorDisabledSubmitButton};
      border-color: ${css.value.borderColorDisabledSubmitButton};
    }
    div#primer-checkout-submit-button-container {
      height: auto;
    }
    .PrimerCheckout__formField:not(:first-of-type) {
      margin-top: unset;
    }
  `
})
</script>

<template>
  <div class="checkout-wrapper">
    <PrimerCheckoutMock
      v-if="isEditor"
      :css="css"
      :submit-label="submitButtonLabel"
      :texts="texts"
    />
    <div v-if="showDefaultLoading">
      <TipTapRenderer v-if="loadingMessageDoc" :content="loadingMessageDoc" />
      <template v-else>{{ CHECKOUT_LOADING_FALLBACK }}</template>
    </div>
    <RenderSlot
      v-if="showLoadingSlot"
      name="loading"
      :children="slotChildren?.loading"
      :editable="editable"
    >
      <div class="slot-placeholder">
        Drop components here — shown while the checkout is loading. Leave empty to show the default
        loading message.
      </div>
    </RenderSlot>
    <div v-if="showDefaultError">
      <TipTapRenderer v-if="errorMessageDoc" :content="errorMessageDoc" />
      <template v-else>{{ CHECKOUT_LOAD_ERROR_FALLBACK }}</template>
    </div>
    <RenderSlot
      v-if="showErrorSlot"
      name="error"
      :children="slotChildren?.error"
      :editable="editable"
    >
      <div class="slot-placeholder">
        Drop components here — shown when the checkout fails to load. Leave empty to show the
        default error message.
      </div>
    </RenderSlot>
    <div id="checkout-container"></div>
  </div>
</template>

<style scoped>
@supports (-moz-appearance: none) {
  .checkout-wrapper input,
  .checkout-wrapper textarea,
  .checkout-wrapper select,
  .checkout-wrapper iframe {
    touch-action: manipulation;
  }
}

.checkout-wrapper {
  position: relative;
  max-width: 450px;
  width: 100%;
  margin: 0 auto;
}

.div#primer-checkout {
  display: unset !important;
}

.slot-placeholder {
  padding: 16px;
  border: 1px dashed var(--color-border, #d6cdbd);
  border-radius: var(--radius-sm, 4px);
  color: var(--color-text-secondary, #7a7368);
  text-align: center;
  font-size: var(--font-size-sm, 14px);
}
</style>
